import { OrgIntegrationService } from './integration-types';

/**
 * LBB = Little Black Book
 * See: /backend/application/littleblackbook/README.md
 */
export interface LBBPipeline {
  id: number;
  label: string;
  stages?: LBBPipelineStage[];
}

export interface LBBPipelineStage {
  id: number;
  label: string;
  isClosed: boolean;
}

export type LBBCustomMetadataValue = number | string | boolean | null;
export interface LBBCustomMetadata {
  [key: string]: LBBCustomMetadataValue;
}

export interface LBBDealOwner {
  id: number;
  avatar: string;
  displayName: string;
  name: string;
  title: string;
}

export interface LBBDeal {
  id: number;
  name: string;
  closeDate: string;
  owner: LBBDealOwner;
  pipelineStage?: LBBPipelineStage;
  pipeline?: LBBPipeline;
  priority: string;
  source: string;
  companies: LBBCompany[];
  contacts: LBBContact[];
  type: string;
}

export interface LBBCompany {
  id: number;
  name: string;
  domain: string;
  industry: string;
  country: string;
  revenue: number;
  sizeEstimateHigh: number;
  sizeEstimateLow: number;
  firstVisitDate: number | null;
  lastVisitDate: number | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmTerm: string | null;
  utmContent: string | null;
  targetAccount: boolean | null;
  idealCustomerProfileTier: string | null;
  location: string;
  customMetadata: LBBCustomMetadata;
}

export interface LBBCompanyOwner {
  id: number;
  companies: {
    data: { id: number; name: string }[];
    hasMore: boolean;
  };
}

export enum LBBContactPhoneNumberKind {
  MOBILE = 'MOBILE',
  WORK = 'WORK',
  HOME = 'HOME',
  FAX = 'FAX',
  OTHER = 'OTHER',
}

export interface LBBContactPhoneNumber {
  label: string;
  number: string;
  nationalNumber: string | null;
  internationalNumber: string | null;
  countryCode: number | null;
  regionCode: string | null;
  isValid: boolean;
  doNotCall: boolean;
  kind: LBBContactPhoneNumberKind;
}

export interface LBBCreateContact {
  firstName?: string | null;
  lastName?: string | null;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  timezone?: string | null;
  customMetadata?: LBBCustomMetadata;
  companyId?: number | null;
  source?: string | null;
}

export interface LBBContact {
  id: number;
  firstName: string | null;
  lastName: string | null;
  name: string;
  email: string | null;
  title: string | null;
  phone: string | null;
  phoneNumbers: LBBContactPhoneNumber[];
  company: LBBCompany | null;
  alert: boolean | null;
  timezone: string | null;
  associatedServices: OrgIntegrationService[];
  customMetadata: LBBCustomMetadata;
  originalVisitorId: string;
  pipeline: LBBPipeline | null;
  pipelineStage: LBBPipelineStage | null;
}

export interface LBBOutboundContact {
  id: number;
  firstName: string | null;
  lastName: string | null;
  name: string;
  title?: string | null;
  email: string | null;
  phone: string | null;
  phoneNumbers: LBBContactPhoneNumber[];
  company: LBBCompany | null;
  timezone?: string | null;
  associatedServices: OrgIntegrationService[];
  customMetadata: Record<string, number | string>;
}

export enum LBBStatTypes {
  allContacts = 'allContacts',
  leadContacts = 'leadContacts',
  allCompanies = 'allCompanies',
  apolloContacts = 'apolloContacts',
  hubspotContacts = 'hubspotContacts',
  outreachContacts = 'outreachContacts',
  salesforceContacts = 'salesforceContacts',
  servicebellContacts = 'servicebellContacts',
}

export type LBBStats = Record<LBBStatTypes, number>;
